import { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { callPost } from '@/api/fetcher';
import { selectAccountSlug } from '@/redux/session/selectors';
import {
  TwilioPhoneNumber,
  TwilioVoiceIntegration,
  VoiceCredentialStatus,
} from '@/shared/types/integration';

import useDebounce from './useDebounce';
import { useDeskId } from './useDeskId';

// Validate Twilio Account SID format: 34 chars starting with AC
const isValidAccountSidFormat = (sid: string): boolean => {
  return /^AC[a-f0-9]{32}$/i.test(sid);
};

const twilioPhonesEndpoint = (slug: string, deskId: string, integrationId: string) =>
  `/www/api/integrations/twilio-voice/${slug}/desks/${deskId}/integrations/${integrationId}/phones`;

/**
 * Hook for managing Twilio Voice integration verification and phone number retrieval with auto-validation
 * @param integrationId - The Twilio Voice integration ID
 * @returns Object containing verification status for each field and phone numbers
 */
export const useTwilioVoice = (integrationId: string) => {
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);

  // Get form values directly from react-hook-form
  const { control } = useFormContext<TwilioVoiceIntegration>();
  const formAccountSid = useWatch({ control, name: 'config.account_sid' });
  const formAuthToken = useWatch({ control, name: 'config.auth_token' });

  // Use debounced values from form to prevent excessive API calls
  const debouncedAccountSid = useDebounce(formAccountSid, 500);
  const debouncedAuthToken = useDebounce(formAuthToken, 1000);

  // Separate validation states for each field
  const [accountSidStatus, setAccountSidStatus] = useState<VoiceCredentialStatus>(
    VoiceCredentialStatus.UNVERIFIED
  );
  const [authTokenStatus, setAuthTokenStatus] = useState<VoiceCredentialStatus>(
    VoiceCredentialStatus.UNVERIFIED
  );
  const [lastVerifiedCredentials, setLastVerifiedCredentials] = useState<string | null>(null);

  const [phones, setPhones] = useState<TwilioPhoneNumber[] | null>(null);

  // Validate Account SID format locally
  useEffect(() => {
    if (!debouncedAccountSid) {
      setAccountSidStatus(VoiceCredentialStatus.UNVERIFIED);
    } else if (isValidAccountSidFormat(debouncedAccountSid)) {
      setAccountSidStatus(VoiceCredentialStatus.VERIFIED);
    } else if (debouncedAccountSid.length > 0) {
      setAccountSidStatus(VoiceCredentialStatus.INVALID);
    }
  }, [debouncedAccountSid]);

  // Create mutation for validating credentials via API
  const validateCredentialsMutation = useMutation({
    mutationFn: async ({
      account_sid,
      auth_token,
    }: {
      account_sid: string;
      auth_token: string;
    }) => {
      return callPost(twilioPhonesEndpoint(slug, deskId, integrationId), {
        account_sid,
        auth_token,
      });
    },
    onSuccess: (data, variables) => {
      setPhones(data);
      setAuthTokenStatus(VoiceCredentialStatus.VERIFIED);
      const currentCredentials = variables.account_sid + variables.auth_token;
      setLastVerifiedCredentials(currentCredentials);
    },
    onError: () => {
      setPhones(null);
      setAuthTokenStatus(VoiceCredentialStatus.INVALID);
    },
  });

  // Validate Auth Token via API when both credentials are ready
  useEffect(() => {
    // Handle when account SID is not verified
    if (accountSidStatus !== VoiceCredentialStatus.VERIFIED) {
      setAuthTokenStatus(VoiceCredentialStatus.UNVERIFIED);
      return;
    }

    if (!debouncedAuthToken) {
      setAuthTokenStatus(VoiceCredentialStatus.UNVERIFIED);
      return;
    }
    if (debouncedAuthToken.length < 32) {
      setAuthTokenStatus(VoiceCredentialStatus.INVALID);
      return;
    }

    // If the credentials are the same as the last verified credentials, restore verified status
    const currentCredentials = debouncedAccountSid + debouncedAuthToken;
    if (lastVerifiedCredentials === currentCredentials) {
      setAuthTokenStatus(VoiceCredentialStatus.VERIFIED);
      return;
    }

    // Don't trigger validation if already in progress
    if (validateCredentialsMutation.isPending) {
      return;
    }

    // Validate auth token via API using mutation
    setAuthTokenStatus(VoiceCredentialStatus.VALIDATING);
    validateCredentialsMutation.mutate({
      account_sid: debouncedAccountSid,
      auth_token: debouncedAuthToken,
    });
    // Don't include the mutation in the dependency => validateCredentialsMutation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    integrationId,
    slug,
    deskId,
    debouncedAccountSid,
    debouncedAuthToken,
    accountSidStatus,
    lastVerifiedCredentials,
  ]);

  // Compute overall verification status
  const verificationStatus = useMemo(() => {
    if (authTokenStatus === VoiceCredentialStatus.VALIDATING) {
      return VoiceCredentialStatus.VALIDATING;
    }
    if (
      accountSidStatus === VoiceCredentialStatus.VERIFIED &&
      authTokenStatus === VoiceCredentialStatus.VERIFIED
    ) {
      return VoiceCredentialStatus.VERIFIED;
    }
    if (
      accountSidStatus === VoiceCredentialStatus.INVALID ||
      authTokenStatus === VoiceCredentialStatus.INVALID
    ) {
      return VoiceCredentialStatus.INVALID;
    }
    return VoiceCredentialStatus.UNVERIFIED;
  }, [accountSidStatus, authTokenStatus]);

  return { accountSidStatus, authTokenStatus, phones, verificationStatus };
};
